import { useCookies } from "react-cookie";
import {
  RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME,
  RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME,
  RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_EXPIRATION,
  RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME,
} from "config/cookies";
import { extractDomain, getExpDateByDays } from "utils/cookiesUtils";
import { chakraUseDisclosure as useDisclosure } from "@airhelp/react-ui-components";
import { trackEvents } from "utils/trackingUtils";
import { useEffect } from "react";
import config from "config/config";
import { useGoogleOptimizeExperimentId } from "contexts/DataContext/hooks";
import { useGoogleOptimize } from "hooks/useGoogleOptimize";

const RETURNING_USERS_VIEW_EVENT = ["ReturningUsers", "viewed", "Notification"];
const EXPERIMENT_NAME = "returning-users-phase-2";

const getFunnelUrl = (
  preEligibilityCookie: string | undefined,
  postEligibilityCookie: string | undefined,
  optimizeVariant: number
) => {
  switch (optimizeVariant) {
    case 0:
      if (postEligibilityCookie) return `${config.funnelUrl}/claim_enquiries/${postEligibilityCookie}`;
      return null;
    case 1:
      if (postEligibilityCookie) return `${config.funnelUrl}/claim_enquiries/${postEligibilityCookie}`;
      if (preEligibilityCookie) return `${config.funnelUrl}/claims/new?restore-pre-eligibility-session=true`;
      return null;
    default:
      if (postEligibilityCookie) return `${config.funnelUrl}/claim_enquiries/${postEligibilityCookie}`;
      return null;
  }
};

const useReturningUsersNotification = () => {
  const experimentId = useGoogleOptimizeExperimentId(EXPERIMENT_NAME);
  const variant = useGoogleOptimize(experimentId);

  const [cookies, setCookie] = useCookies([
    RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME,
    RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME,
    RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME,
  ]);

  const funnelUrl = getFunnelUrl(
    cookies[RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME],
    cookies[RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME],
    Number(variant)
  );

  const hasFunnelCookie =
    cookies[RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME] || cookies[RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME];
  const notificationClosedCookie = cookies[RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME];
  const shouldBeOpen = funnelUrl && hasFunnelCookie && !notificationClosedCookie;

  const { isOpen, onClose: onDisclosureClose, onOpen: onDisclosureOpen } = useDisclosure({ defaultIsOpen: false });

  const onClose = () => {
    onDisclosureClose();
    setCookie(RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME, "true", {
      domain: extractDomain(),
      path: "/",
      expires: getExpDateByDays(RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_EXPIRATION),
    });
  };

  useEffect(() => {
    if (shouldBeOpen) {
      onDisclosureOpen();
      trackEvents({
        gtmEvent: RETURNING_USERS_VIEW_EVENT,
        mixpanelEvent: undefined,
      });
    }
  }, [shouldBeOpen, onDisclosureOpen]);

  return { isOpen: isOpen && funnelUrl, onClose, funnelUrl };
};

export default useReturningUsersNotification;
